const formatDate = (date: Date): string => {
  const dateWithoutTimezone = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  // Convert date to ISO string and remove Z
  const isoString = dateWithoutTimezone.toISOString().slice(0, -1);
  // Get timezone offset in minutes
  const timezoneOffset = date.getTimezoneOffset();
  // Convert timezone offset to hours and minutes
  const timezoneOffsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
  const timezoneOffsetMinutes = Math.abs(timezoneOffset) % 60;

  // Format timezone offset as string
  const timezoneOffsetString =
    (timezoneOffset < 0 ? '+' : '-') +
    String(timezoneOffsetHours).padStart(2, '0') +
    ':' +
    String(timezoneOffsetMinutes).padStart(2, '0');
  // Append timezone offset to ISO string
  return isoString + timezoneOffsetString;
};

const slotToDate = (slot: string | Date): Date => {
  if (slot instanceof Date) {
    const stringDate = formatDate(slot);
    return new Date(stringDate.substring(0, 23));
  } else {
    return new Date(slot.substring(0, 23));
  }
};

export const slotsAreEqual = (oneSlot: string | Date, anotherSlot: string | Date): boolean => {
  const dateOne = slotToDate(oneSlot);
  const dateTwo = slotToDate(anotherSlot);

  return dateOne.getTime() === dateTwo.getTime();
};

export const getTimeSlotWithoutTimezone = (slot: Date | string): Date => {
  if (slot instanceof Date) {
    return slot;
  }

  return new Date(slot.substring(0, 23));
};

export const formatTime = (time: Date | string): string =>
  time.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

/**
 * Validates if an end time is valid relative to a start time
 *
 * @param startTime - The start time as Date, string, or any valid date input
 * @param endTime - The end time to validate (can be null, undefined, Date, or string)
 * @returns true if the end time is valid, false otherwise
 *
 * @example
 * ```typescript
 * // Valid end time
 * isValidEndTime('2024-01-15T10:00:00Z', '2024-01-15T11:00:00Z'); // true
 *
 * // Invalid: end time before start time
 * isValidEndTime('2024-01-15T10:00:00Z', '2024-01-15T09:00:00Z'); // false
 *
 * // Invalid: null end time
 * isValidEndTime('2024-01-15T10:00:00Z', null); // false
 *
 * // Invalid: undefined end time
 * isValidEndTime('2024-01-15T10:00:00Z', undefined); // false
 * ```
 */
export const isValidEndTime = (
  startTime: Date | string,
  endTime: Date | string | null | undefined,
): boolean => {
  // Rule 1: End time must exist and not be null/undefined
  if (!endTime) {
    return false;
  }

  try {
    // Rule 2: Both start time and end time must be valid dates
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return false;
    }

    // Rule 3: End time must be after start time
    if (endDate <= startDate) {
      return false;
    }

    return true;
  } catch (error) {
    // Handle any unexpected errors during date parsing
    return false;
  }
};
